import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import {
    getRepositoryQueryKey,
    useRepositoryMutation,
    DetailedPlatformProfileResult,
    PlatformProfileUpdateParameters,
} from '@mews-commander/data-access';
import {
    createZodFormResolver,
    LocalizedText,
    logSentryError,
    showSuccessAlert,
    useFormControls,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { parsePlatformProfilePrivilegesToArray } from '../../Common';
import {
    CommonPlatformProfileFormData,
    FieldNames,
    parsePlatformProfilePrivilegesToApiPayload,
    platformProfilesCommonValidationSchema,
    PlatformProfilesRole,
} from '../../CommonPlatformProfileForm';

export const useEditPlatformProfileForm = ({
    platformProfile,
    onClose,
}: {
    platformProfile: DetailedPlatformProfileResult;
    onClose: () => void;
}) => {
    const platformProfileId = platformProfile.profileId;
    const defaultValues = parsePlatformProfileToFormData(platformProfile);

    const queryClient = useQueryClient();
    const addProfileMutation = useRepositoryMutation('platformProfiles', 'update', {
        invalidateQueries: { platformProfiles: ['getAll'] },
    });
    const dispatch = useDispatch();

    return useFormControls<CommonPlatformProfileFormData>({
        defaultValues,
        resolver: createZodFormResolver(platformProfilesCommonValidationSchema),
        onSubmit: async (formData) => {
            try {
                const payload = formatFormData(platformProfileId, formData);
                await addProfileMutation.mutateAsync(payload);

                // TODO: Adjust invalidateQueries to support multiple query invalidations
                void queryClient.invalidateQueries({
                    queryKey: getRepositoryQueryKey('platformProfiles', 'get', { platformProfileId }),
                });

                onClose();
                showSuccessAlert({
                    description: (
                        <LocalizedText
                            textKey={TextKey.PlatformProfileEditSavedDescription}
                            substitutes={{ name: platformProfile.fullName }}
                        />
                    ),
                });
            } catch (error) {
                dispatch(logSentryError({ error }));
            }
        },
    });
};

const parsePlatformProfileToFormData = (
    platformProfile: DetailedPlatformProfileResult
): CommonPlatformProfileFormData => ({
    [FieldNames.Role]: platformProfile.role as PlatformProfilesRole,
    [FieldNames.Privileges]: parsePlatformProfilePrivilegesToArray(platformProfile.platformProfilePrivileges),
});

const formatFormData = (
    platformProfileId: string,
    { privileges, role }: CommonPlatformProfileFormData
): PlatformProfileUpdateParameters => ({
    platformProfileId,
    isAdmin: role === PlatformProfilesRole.Admin,
    privileges: parsePlatformProfilePrivilegesToApiPayload(role, privileges),
});
